'use client';

import { useState, useEffect } from 'react';
import type { FormValues, FormErrors, FormTouched, FormInstance } from '../types';
import { loadFormState } from '../utils/storage';

interface UseFormStateReturn {
  values: FormValues;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  touched: FormTouched;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  completedForms: Record<string, FormInstance[]>;
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
}

/**
 * Custom hook for managing form state
 */
export function useFormState(
  localStorageKey: string,
  stepsById: Record<string, unknown>
): UseFormStateReturn {
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>({});
  const [completedForms, setCompletedForms] = useState<Record<string, FormInstance[]>>({});

  // Load persisted state
  useEffect(() => {
    const stored = loadFormState(localStorageKey);
    if (stored?.values) {
      setValues(stored.values);
    }
    if (stored?.completedForms) {
      setCompletedForms(stored.completedForms);
    }
  }, [localStorageKey, stepsById]);

  return {
    values,
    setValues,
    errors,
    setErrors,
    touched,
    setTouched,
    completedForms,
    setCompletedForms,
  };
}
